import { CardCvcElement, CardExpiryElement, CardNumberElement, Elements } from "@stripe/react-stripe-js";
import { StripeCardNumberElementChangeEvent, StripeElementChangeEvent, StripeElementStyle } from "@stripe/stripe-js";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import constants from "../../../../constants";
import { getStripe } from "../../../../services/stripe";
import { classNames } from "../../../../utils/styles";
import { CardBrand, CardBrandMark } from "./icons";
import { CardForm } from "./styles";

// The card's number, expiry and CVC are Stripe's own fields (iframes from js.stripe.com): the card data goes
// straight to Stripe, never through Keepo. Only the name on the card is a field of ours

/** Stripe.js for everything under it (the checkout and the screens that change the card). */
export const StripeProvider = ({ children }: { children: ReactNode }) => (
  <Elements
    stripe={getStripe()}
    options={{ fonts: [{ cssSrc: "https://fonts.googleapis.com/css2?family=Inter:wght@500;600&display=swap" }] }}>
    {children}
  </Elements>
);

type FieldState = { complete: boolean; error?: string };

export type CardState = {
  number: FieldState;
  expiry: FieldState;
  cvc: FieldState;
  brand?: string;
  name: string;
};

export const EMPTY_CARD: CardState = {
  number: { complete: false },
  expiry: { complete: false },
  cvc: { complete: false },
  name: "",
};

/** What's wrong: Stripe's own message for what it found invalid, else a key under `checkout.card.errors`. */
export type CardErrors = Partial<Record<"number" | "expiry" | "cvc" | "name", string>>;

const ERROR_KEYS = ["number", "expiry", "cvc", "name"];

export const validateCard = (card: CardState): CardErrors => {
  const errors: CardErrors = {};
  (["number", "expiry", "cvc"] as const).forEach(field => {
    if (!card[field].complete) {
      errors[field] = card[field].error || field;
    }
  });
  if (card.name.trim().split(/\s+/).length < 2) {
    errors.name = "name";
  }
  return errors;
};

// The look of our fields (see fieldGridCss), inside Stripe's iframes
const ELEMENT_STYLE: StripeElementStyle = {
  base: {
    color: "#ffffff",
    fontFamily: "Inter, sans-serif",
    fontSize: "15px",
    fontWeight: "600",
    lineHeight: "46px",
    "::placeholder": { color: "#5e6272", fontWeight: "500" },
  },
  invalid: { color: "#ff4264" },
};

const STRIPE_BRANDS: Record<string, CardBrand> = { visa: "visa", mastercard: "mastercard", amex: "amex" };

type Props = {
  card: CardState;
  onChange: (card: CardState) => void;
  errors: CardErrors;
  disabled?: boolean;
};

export const StripeCardFields = ({ card, onChange, errors, disabled }: Props) => {
  const { t } = useTranslation();
  const prefix = "pages.subscription.checkout.card";

  // No publishable key in this build: Stripe can't show its fields, say so instead of empty boxes
  if (!constants.stripePublishableKey) {
    return (
      <p className="info" role="alert">
        {t("pages.subscription.checkout.errors.unavailable")}
      </p>
    );
  }

  const errorOf = (field: keyof CardErrors) => {
    const error = errors[field];
    // Stripe's messages come translated already; ours are keys
    return error && ERROR_KEYS.includes(error) ? t(`${prefix}.errors.${error}`) : error;
  };

  const track =
    (field: "number" | "expiry" | "cvc") => (event: StripeElementChangeEvent | StripeCardNumberElementChangeEvent) =>
      onChange({
        ...card,
        [field]: { complete: event.complete, error: event.error?.message },
        ...(field === "number" ? { brand: (event as StripeCardNumberElementChangeEvent).brand } : {}),
      });

  const brand = card.brand ? STRIPE_BRANDS[card.brand] : undefined;

  return (
    <CardForm>
      <div className={classNames("field full", { invalid: !!errors.number })}>
        <span className="label">{t(`${prefix}.number`)}</span>
        <span className="control">
          <CardNumberElement
            className="stripe-field"
            // No Stripe Link button inside the field: the card is typed (or autofilled by the browser)
            options={{
              style: ELEMENT_STYLE,
              placeholder: "1234 1234 1234 1234",
              showIcon: false,
              disableLink: true,
              disabled,
            }}
            onChange={track("number")}
          />
          {brand && <CardBrandMark brand={brand} />}
        </span>
        {errorOf("number") && <small>{errorOf("number")}</small>}
      </div>
      <div className={classNames("field", { invalid: !!errors.expiry })}>
        <span className="label">{t(`${prefix}.expiry`)}</span>
        <span className="control">
          <CardExpiryElement
            className="stripe-field"
            options={{ style: ELEMENT_STYLE, placeholder: t(`${prefix}.expiryPlaceholder`), disabled }}
            onChange={track("expiry")}
          />
        </span>
        {errorOf("expiry") && <small>{errorOf("expiry")}</small>}
      </div>
      <div className={classNames("field", { invalid: !!errors.cvc })}>
        <span className="label">{t(`${prefix}.cvc`)}</span>
        <span className="control">
          <CardCvcElement
            className="stripe-field"
            options={{ style: ELEMENT_STYLE, placeholder: card.brand === "amex" ? "1234" : "123", disabled }}
            onChange={track("cvc")}
          />
        </span>
        {errorOf("cvc") && <small>{errorOf("cvc")}</small>}
      </div>
      <label className={classNames("field full", { invalid: !!errors.name })}>
        <span className="label">{t(`${prefix}.name`)}</span>
        <span className="control">
          <input
            autoComplete="cc-name"
            placeholder={t(`${prefix}.namePlaceholder`)}
            value={card.name}
            onChange={e => onChange({ ...card, name: e.target.value.toUpperCase() })}
          />
        </span>
        {errorOf("name") && <small>{errorOf("name")}</small>}
      </label>
    </CardForm>
  );
};
